<template>
  <div class="p-company-settings-company-profile">
    <VMessage
      type="warning"
      :fullwidth="true"
      v-if="!permissionCompany">
      <template v-slot:icon>
        <ExclamationTriangleIcon />
      </template>
      <template v-slot:text>
        {{ $t('companySettings.companyProfile.permissionWarning') }}
      </template>
    </VMessage>
    <VForm
    :onSubmit="validateForm"
    data-vv-scope="company-edit"
    v-if="companyData && permissionCompany === true && !loading">
      <div slot="row">
        <VGrid>
          <div slot="gridItem" type="6">
            <b>{{ $t('companySettings.companyProfile.companyName') }}</b>
            <VInput
              type="text"
              data-ec="company_profile"
              data-et="company_name_input"
              v-model="companyData.name"
              :placeholder="$t('companySettings.companyProfile.companyName')"
              v-validate="'required'"
              data-vv-name="company-name"
              :data-vv-as="$t('companySettings.companyProfile.companyName')"
              data-vv-scope="company-edit"
              :class="{ 'a-input--error': errors.has('company-edit.company-name') }"
              data-hj-suppress
            />
            <VMessage
              type="error"
              subtype="basic"
              v-show="errors.has('company-edit.company-name')">
              <template v-slot:icon>
                <XMarkIcon />
              </template>
              <template v-slot:text>
                {{ errors.first('company-edit.company-name') }}
              </template>
            </VMessage>
          </div>

          <div slot="gridItem" type="6">
            <b>{{ $t('companySettings.companyProfile.addressLineOne') }}</b>
            <VInput
              type="text"
              data-test="company_profile"
              data-et="address_line1_input"
              v-model="companyData.address_line_1"
              placeholder="e.g. 3 Unity Street"
              data-hj-suppress
            />
          </div>
        </VGrid>
      </div>
      <div slot="row">
        <VGrid>
          <div slot="gridItem" type="6">
            <b>{{ $t('companySettings.companyProfile.emailAddress') }}</b>
            <VInput
              type="text"
              data-test="company_profile"
              data-et="email_input"
              v-model.trim="companyData.email"
              placeholder="e.g. hello@example.com"
              v-validate="'required|isTrimmedEmail'"
              data-vv-name="email-address"
              :data-vv-as="$t('companySettings.companyProfile.emailAddress')"
              data-vv-scope="company-edit"
              :class="{ 'a-input--error': errors.has('company-edit.email-address') }"
              data-hj-suppress
            />
          </div>
          <VMessage
            type="error"
            subtype="basic"
            v-show="errors.has('company-edit.email-address')">
            <template v-slot:icon>
              <XMarkIcon />
            </template>
            <template v-slot:text>
              {{ errors.first('company-edit.email-address') }}
            </template>
          </VMessage>
          <div slot="gridItem" type="6">
            <b>{{ $t('companySettings.companyProfile.addressLineTwo') }} {{ $t('functional.optional') }}</b>
            <VInput
              type="text"
              data-test="company_profile"
              data-et="address_line2_input"
              :data-vv-as="$t('companySettings.companyProfile.addressLineTwo')"
              v-model="companyData.address_line_2"
              data-hj-suppress
            />
          </div>
        </VGrid>
      </div>
      <div slot="row">
        <VGrid>
          <div slot="gridItem" type="6">
            <b>{{ $t('companySettings.companyProfile.vatNumber') }}</b>
            <VInput
              type="text"
              data-test="company_profile"
              data-et="vat_number_input"
              v-model="companyData.vat"
              data-hj-suppress
            />
          </div>
          <div slot="gridItem" type="6">
            <b>{{ $t('companySettings.companyProfile.town') }} {{ $t('functional.optional') }}</b>
            <VInput
              type="text"
              data-test="company_profile"
              data-et="address_town_input"
              v-model="companyData.address_town"
              data-hj-suppress
            />
          </div>
        </VGrid>
      </div>
      <div slot="row">
        <VGrid>
          <div slot="gridItem" type="6">
            <b>{{ $t('companySettings.companyProfile.accountingEmailAddress') }}</b>
            <VInput
              type="text"
              data-test="company_profile"
              data-et="accounting_email_input"
              v-model.trim="companyData.account_email"
              :placeholder="$t('companySettings.companyProfile.accountingEmailAddress')"
              v-validate="'isTrimmedEmail'"
              data-vv-name="accounting-email-address"
              :data-vv-as="$t('companySettings.companyProfile.accountingEmailAddress')"
              data-vv-scope="company-edit"
              :class="{ 'a-input--error': errors.has('company-edit.accounting-email-address') }"
              data-hj-suppress
            />
            <VMessage
              type="error"
              subtype="basic"
              v-show="errors.has('company-edit.accounting-email-address')">
              <template v-slot:icon>
                <XMarkIcon />
              </template>
              <template v-slot:text>
                {{ errors.first('company-edit.accounting-email-address') }}
              </template>
            </VMessage>
          </div>
          <div slot="gridItem" type="3">
            <b>{{ $t('companySettings.companyProfile.city') }}</b>
            <VInput
              type="text"
              data-test="company_profile"
              data-et="address_city_input"
              v-model="companyData.address_city"
              placeholder="e.g Bristol"
              data-hj-suppress
            />
          </div>
          <div slot="gridItem" type="3">
            <b>{{ $t('companySettings.companyProfile.postcode') }}</b>
            <VInput
              type="text"
              data-test="company_profile"
              data-et="address_postcode_input"
              v-model="companyData.address_postcode"
              placeholder="e.g BS1 5HH"
              data-hj-suppress
            />
          </div>
        </VGrid>
      </div>
      <div slot="row">
        <VGrid>
          <div slot="gridItem" type="6">
            <b>{{ $t('companySettings.companyProfile.telephoneNumber') }}</b>
            <VInput
              type="text"
              v-validate="'isPhoneNumber'"
              data-vv-scope="company-edit"
              data-vv-name="telephone"
              data-test="company_profile"
              data-et="phone_number_input"
              :data-vv-as="$t('register.telephone')"
              v-model="companyData.telephone"
              placeholder="e.g +44 01234 56789"
              data-hj-suppress
            />
            <VMessage
              type="error"
              subtype="basic"
              v-show="errors.has('company-edit.telephone')">
              <template v-slot:icon>
                <XMarkIcon />
              </template>
              <template v-slot:text>
                {{ errors.first('company-edit.telephone') }}
              </template>
            </VMessage>
          </div>
          <div slot="gridItem" type="6">
            <b>{{ $t('companySettings.companyProfile.country') }}</b>
            <ElSelect class="country-input" size="large" v-model="companyData.country_code" :disabled="companyData.currency_locked">
              <ElOption
                v-for="country in countries"
                :value="country.code"
                :key="country.code"
                data-ec="company_profile"
                :data-et="`country_selected_${country.name}`"
                :label="country.name"
              />
            </ElSelect>
            <p class="country-locked-text" v-if="companyData.currency_locked">{{ $t('companySettings.companyProfile.countryLocked') }}</p>
          </div>
        </VGrid>
      </div>
      <div slot="formButtons">
        <VButton
            type="cta-1"
            subtype="submit"
            class="float-right"
            data-ec="company_profile"
            data-et="submit_button"
            href="#">
          <CheckIcon />
          <span>{{ $t('functional.save') }}</span>
        </VButton>
      </div>
    </VForm>
    <VLoadingSpinner :overlay="false" style="margin-top: 96px;" v-if="permissionCompany && loading" />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import VButton from '@/components/deprecated/atoms/Button.vue';
import VInput from '@/components/deprecated/atoms/Input.vue';
import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VForm from '@/components/deprecated/organisms/Form.vue';
import VGrid from '@/components/deprecated/organisms/Grid.vue';
import { CheckIcon, ExclamationTriangleIcon, XMarkIcon } from '@/components/icons/fontIcons';

export default {
  name: 'CompanyProfile',
  inject: {
    $validator: '$validator',
  },
  components: {
    VButton,
    VInput,
    VLoadingSpinner,
    VMessage,
    VForm,
    VGrid,
    CheckIcon,
    ExclamationTriangleIcon,
    XMarkIcon,
  },
  data() {
    return {
      loading: true,
    };
  },
  created() {
    this.$store.dispatch('company/GET').finally(() => { this.loading = false; });
    this.$store.dispatch('countries/GET');
  },
  computed: {
    ...mapState({
      companyData: state => state.company.company,
      countries: state => state.countries.countries,
    }),
    ...mapGetters({
      userHasRole: 'roles/userHasRole',
    }),
    permissionCompany() {
      return this.userHasRole('super-admin');
    },
  },
  methods: {
    validateForm(scope) {
      this.$validator.validateAll(scope).then((validated) => {
        if (validated) {
          this.loading = true;
          this.$store.dispatch('company/UPDATE', this.companyData).finally(() => { this.loading = false; });
        }
      });
    },
  },
};
</script>

<style scoped>
.country-locked-text {
  font-size: var(--font-size-xs);
  font-style: italic;
  margin-top: 5px;
  color: var(--signable-secondary-3-shade-1);
}
.country-input {
  width: 100%;
}
</style>
